import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { employeeApi } from '../services/employeeApi';
import { EmployeeHRCost } from '../types/hrCost';
import { calculatedSummaryApi, HRCostSummary } from '../services/calculatedSummaryApi';
import { useAlert } from '../contexts/AlertContext';

// Helper function to format numbers with commas for currency
const formatCurrency = (value: number | undefined): string => {
  if (typeof value !== 'number' || isNaN(value)) return '0';
  return new Intl.NumberFormat('ko-KR').format(Math.round(value));
};

// Helper function to round up to millions place (백만의 자리에서 올림) - Excel ROUNDUP(Y7,-6) equivalent
const formatRoundUpToMillions = (value: number | undefined): string => {
  if (typeof value !== 'number' || isNaN(value)) return '0';
  const rounded = Math.ceil(value / 1000000) * 1000000; // Round up to nearest million
  return new Intl.NumberFormat('ko-KR').format(rounded);
};

// 직급 순서 정의 (높은 직급부터) - EmployeeForm 순서와 동일
const positionOrder = [
  '대표이사',
  '부장',
  '차장',
  '과장',
  '대리',
  'PRINCIPAL', 'Principal',
  'DIRECTOR', 'Director', '디렉터',
  'MANAGER', 'Manager', 'PM', '매니저', '관리자',
  'SENIOR', 'SR', 'Senior', '선임', 'SBA', 'Senior Business Analyst',
  'BUSINESS ANALYST', 'Business Analyst',
  'ASSOCIATE', 'Associate',
  'JUNIOR', 'JR', 'Junior', '사원',
  'RA', '인턴'
];

// 정렬 타입 정의
type SortType = 'position' | 'name' | 'joinDate' | 'salary' | 'monthlyBurden' | 'bonus' | 'monthlyLaborCost' | 'finalCost';
type SortOrder = 'asc' | 'desc';

// 요약 테이블 헤더 (기본 표시)
const summaryHeaders = [
  "순번",
  "연도",
  "이름", 
  "직급",
  "인력원가(반올림)",
  "입사일", 
  "연봉", 
  "월 부담액", 
  "상여금",
  "EPS",
  "월 인력비", 
  "인력원가"
];

// 전체 테이블 헤더 (상세 표시)
const detailHeaders = [
  "순번",
  "이름", 
  "직급",
  "인력원가(반올림)",
  "입사일", 
  "연봉", 
  "4대보험/퇴직금", 
  "회사 부담금액", 
  "월 부담액", 
  "상여 기준일", 
  "상여금 비율", 
  "PS 기준일", 
  "PS 비율", 
  "상여금", 
  "복지비용", 
  "고정 인건비", 
  "월 인력비", 
  "OPEX 배분", 
  "EPS", 
  "Monthly EPS", 
  "ECM", 
  "인력원가"
];

const HRCostManagement: React.FC = () => {
  const { showToast } = useAlert();
  // 상태 관리
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 셀 선택 상태
  const [selectedCell, setSelectedCell] = useState<{row: number, col: number} | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  
  // 현재 날짜 기준
  const today = new Date();
  const currentYear = today.getFullYear();
  
  // 사용 가능한 연도 목록 상태
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  
  // 선택된 연도/월 상태 - 기본값은 현재 연도
  const [selectedYear, setSelectedYear] = useState(currentYear);
  
  // 기준일 설정 (선택된 연도 기준)
  const bonusRefDate = `${selectedYear}-06-30`;  // 상여금 기준일: 매년 6월 30일
  const psRefDate = `${selectedYear}-12-31`;      // 성과급 기준일: 매년 12월 31일

  // HR Unit Cost 데이터 로딩
  const [hrCostData, setHRCostData] = useState<EmployeeHRCost[]>([]);
  
  // 계산된 요약 데이터 (DB에서 가져옴)
  const [calculatedTotals, setCalculatedTotals] = useState<HRCostSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  
  // 상세 표시 모드 상태
  const [showDetailView, setShowDetailView] = useState(true);
  
  // 정렬 상태 - 초기값을 null로 설정하여 백엔드 정렬 유지
  const [sortField, setSortField] = useState<SortType | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  
  // EPS/ECM 비율 상태 관리
  const [epsRate, setEpsRate] = useState(30); // 기본값 30%
  const [ecmRate, setEcmRate] = useState(50); // 기본값 50%
  const [tempEpsRate, setTempEpsRate] = useState(30); // 임시 입력값
  const [tempEcmRate, setTempEcmRate] = useState(50); // 임시 입력값
  
  // 편집 상태 관리
  const [editingCell, setEditingCell] = useState<{itemId: number, field: 'bonus' | 'eps'} | null>(null);
  const [editValues, setEditValues] = useState<{[key: string]: number}>({});
  
  // 셀 선택 및 키보드 네비게이션 핸들러
  const handleCellClick = useCallback((rowIndex: number, colIndex: number) => {
    setSelectedCell({ row: rowIndex, col: colIndex });
  }, []);
  
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!selectedCell || hrCostData.length === 0) return;
    
    const totalRows = hrCostData.length + 1; // +1 for total row
    const totalCols = showDetailView ? detailHeaders.length : summaryHeaders.length;
    
    let newRow = selectedCell.row;
    let newCol = selectedCell.col;
    
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        newRow = Math.max(0, selectedCell.row - 1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        newRow = Math.min(totalRows - 1, selectedCell.row + 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        newCol = Math.max(0, selectedCell.col - 1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        newCol = Math.min(totalCols - 1, selectedCell.col + 1);
        break;
      case 'Home':
        e.preventDefault();
        if (e.ctrlKey) {
          newRow = 0;
          newCol = 0;
        } else {
          newCol = 0;
        }
        break;
      case 'End':
        e.preventDefault();
        if (e.ctrlKey) {
          newRow = totalRows - 1;
          newCol = totalCols - 1;
        } else {
          newCol = totalCols - 1;
        }
        break;
      default:
        return;
    }
    
    setSelectedCell({ row: newRow, col: newCol });
  }, [selectedCell, hrCostData.length, showDetailView]);
  
  // 테이블 포커스 및 스크롤 설정
  useEffect(() => {
    if (selectedCell && tableRef.current) {
      // 테이블에 포커스 설정
      tableRef.current.focus();
      
      // 선택된 셀 찾기
      let selectedCellElement: HTMLElement | null = null;
      
      // 첫 번째 셀(합계 셀)의 경우 colSpan 처리
      if (selectedCell.row === hrCostData.length && selectedCell.col <= 2) {
        // 합계 행의 첫 번째 셀 (범위: 0, 1, 2 인덱스 모두 첫 번째 셀로 매핑)
        selectedCellElement = tableRef.current.querySelector(
          `td[data-row="${selectedCell.row}"][data-col="0"]`
        ) as HTMLElement;
      } else {
        // 일반 셀
        selectedCellElement = tableRef.current.querySelector(
          `td[data-row="${selectedCell.row}"][data-col="${selectedCell.col}"]`
        ) as HTMLElement;
      }
      
      if (selectedCellElement) {
        // 부드러운 스크롤로 해당 셀이 화면에 보이도록 이동
        setTimeout(() => {
          if (selectedCellElement) {
            selectedCellElement.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',    // 수직으로 최소 이동
              inline: 'nearest'    // 수평으로 최소 이동
            });
          }
        }, 50); // 약간의 딩레이로 DOM 업데이트 완료 대기
      }
    }
  }, [selectedCell, hrCostData.length]);
  
  // 셀 스타일 반환 함수
  const getCellStyle = useCallback((rowIndex: number, colIndex: number, isTotal: boolean = false) => {
    const isSelected = selectedCell?.row === rowIndex && selectedCell?.col === colIndex;
    const baseClasses = "px-3 py-3 whitespace-nowrap relative";
    
    if (isSelected) {
      return `${baseClasses} bg-blue-100`;
    }
    
    if (isTotal) {
      return `${baseClasses} bg-gray-100 font-bold text-gray-900`;
    }
    
    return `${baseClasses} hover:bg-gray-50 transition-colors`;
  }, [selectedCell]);
  
  // HR Cost 데이터 로딩 함수
  const fetchHRCostData = async () => {
    try {
      setLoading(true);
      
      // EPS/ECM 비율 설정 가져오기
      try {
        const hrCostSettings = await employeeApi.getHRCostSettings(selectedYear);
        const epsValue = hrCostSettings.epsRate * 100; // 소수를 백분율로 변환 (0.30 -> 30)
        const ecmValue = hrCostSettings.ecmRate * 100; // 소수를 백분율로 변환 (0.50 -> 50)
        setEpsRate(epsValue);
        setEcmRate(ecmValue);
        setTempEpsRate(epsValue);
        setTempEcmRate(ecmValue);
      } catch (error) {
        setEpsRate(30);
        setEcmRate(50);
        setTempEpsRate(30);
        setTempEcmRate(50);
      }
      
      // Employee HR Cost 데이터 가져오기 (DB에서 직접 조회)
      const employeeHRData = await employeeApi.getAllHRCostByYear(selectedYear);
      
      // Employee HR Cost 데이터를 EmployeeHRCost 형식으로 변환
      const hrData = employeeHRData.map(item => ({
        id: item.hrCost.id,
        employeeId: item.employee.id,
        year: selectedYear,
        annualSalary: item.hrCost.annualSalary,
        joinDate: item.employee.joinDate || item.hrCost.joinDate,
        bonusBaseDate: item.hrCost.bonusBaseDate,
        performanceBaseDate: item.hrCost.performanceBaseDate,
        insuranceRetirement: item.hrCost.insuranceRetirement,
        companyBurden: item.hrCost.companyBurden,
        monthlyBurden: item.hrCost.monthlyBurden,
        bonusBaseDays: item.hrCost.bonusBaseDays,
        bonusRate: item.hrCost.bonusRate,
        adjustedBonusRate: item.hrCost.adjustedBonusRate,
        performanceBaseDays: item.hrCost.performanceBaseDays,
        performanceRate: item.hrCost.performanceRate,
        adjustedPerformanceRate: item.hrCost.adjustedPerformanceRate,
        bonusAmount: item.hrCost.bonusAmount,
        welfareCost: item.hrCost.welfareCost,
        fixedLaborCost: item.hrCost.fixedLaborCost,
        monthlyLaborCost: item.hrCost.monthlyLaborCost,
        opexAllocation: item.hrCost.opexAllocation,
        eps: item.hrCost.eps,
        monthlyEps: item.hrCost.monthlyEps,
        ecm: item.hrCost.ecm,
        finalLaborCost: item.hrCost.finalLaborCost,
        roleMultiplier: item.hrCost.roleMultiplier,
        adjustedMonthlyLaborCost: item.hrCost.adjustedMonthlyLaborCost,
        adjustedFinalLaborCost: item.hrCost.adjustedFinalLaborCost,
        calculationDate: item.hrCost.calculationDate || item.hrCost.createdAt,
        memo: item.hrCost.memo,
        employee: item.employee,
        createdAt: item.hrCost.createdAt,
        updatedAt: item.hrCost.updatedAt,
      }));
      
      setHRCostData(hrData);
      setError(null);
      
      // 계산된 요약 데이터 가져오기 (업데이트는 하지 않음)
      setSummaryLoading(true);
      try {
        const summaryData = await calculatedSummaryApi.getHRCostYearlySummary(selectedYear);
        if (summaryData) {
          setCalculatedTotals(summaryData.calculatedData as HRCostSummary);
        } else {
          setCalculatedTotals(null);
        }
      } catch (summaryError) {
        setCalculatedTotals(null);
      } finally {
        setSummaryLoading(false);
      }
      
    } catch (err) {
      setError('데이터를 불러오는데 실패했습니다.');
      showToast('데이터를 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 사용 가능한 연도 목록 가져오기
  useEffect(() => {
    const fetchAvailableYears = async () => {
      try {
        const years = await employeeApi.getAllYears();
        const yearList = years.map(y => y.year).sort((a, b) => b - a); // 내림차순 정렬
        setAvailableYears(yearList);
        
        // 현재 연도가 목록에 없으면 첫 번째 연도를 선택
        if (yearList.length > 0 && !yearList.includes(currentYear)) {
          setSelectedYear(yearList[0]);
        }
      } catch (error) {
        // 에러 시 현재 연도만 사용
        setAvailableYears([currentYear]);
      }
    };
    
    fetchAvailableYears();
  }, []); // 한 번만 실행

  useEffect(() => {
    if (selectedYear) {
      fetchHRCostData();
    }
  }, [selectedYear]);

  // 정렬 함수
  const sortData = useCallback((field: SortType, order: SortOrder) => {
    // 백엔드에서 이미 status별로 정렬된 데이터를 받으므로, 
    // status 정렬을 유지하면서 같은 status 내에서만 필드별 정렬 적용
    return [...hrCostData].sort((a, b) => {
      // 1. 먼저 status로 정렬 (ACTIVE 먼저, VIRTUAL 나중에)
      const aIsVirtual = a.employee?.status === 'VIRTUAL';
      const bIsVirtual = b.employee?.status === 'VIRTUAL';
      
      if (aIsVirtual !== bIsVirtual) {
        return aIsVirtual ? 1 : -1; // ACTIVE가 먼저 오도록
      }
      
      // 2. 같은 status 내에서만 필드별 정렬 적용
      let aValue: any;
      let bValue: any;

      switch (field) {
        case 'position':
          // 직급 순서에 따라 정렬
          const aIndex = positionOrder.findIndex(pos => 
            (a.employee?.position || '').toUpperCase().includes(pos.toUpperCase())
          );
          const bIndex = positionOrder.findIndex(pos => 
            (b.employee?.position || '').toUpperCase().includes(pos.toUpperCase())
          );
          aValue = aIndex === -1 ? 999 : aIndex;
          bValue = bIndex === -1 ? 999 : bIndex;
          break;
        case 'name':
          aValue = a.employee?.name || '';
          bValue = b.employee?.name || '';
          break;
        case 'joinDate':
          aValue = a.joinDate ? new Date(a.joinDate).getTime() : 0;
          bValue = b.joinDate ? new Date(b.joinDate).getTime() : 0;
          break;
        case 'salary':
          aValue = Number(a.annualSalary) || Number(a.employee?.annualSalary) || 0;
          bValue = Number(b.annualSalary) || Number(b.employee?.annualSalary) || 0;
          break;
        case 'monthlyBurden':
          aValue = a.monthlyBurden || 0;
          bValue = b.monthlyBurden || 0;
          break;
        case 'bonus':
          aValue = a.bonusAmount || 0;
          bValue = b.bonusAmount || 0;
          break;
        case 'monthlyLaborCost':
          aValue = a.monthlyLaborCost || 0;
          bValue = b.monthlyLaborCost || 0;
          break;
        case 'finalCost':
          aValue = a.adjustedFinalLaborCost || a.finalLaborCost || 0;
          bValue = b.adjustedFinalLaborCost || b.finalLaborCost || 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        return order === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return order === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }
    });
  }, [hrCostData]);

  // 정렬 핸들러
  const handleSort = (field: SortType) => {
    // 같은 필드를 다시 클릭하면 정렬 순서 변경
    // 다른 필드를 클릭하면 해당 필드로 asc 정렬
    if (sortField === field) {
      if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else {
        // desc 다음에 다시 클릭하면 정렬 해제 (백엔드 정렬로 복원)
        setSortField(null);
        setSortOrder('asc');
      }
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // 편집 시작 핸들러
  const handleEditStart = (itemId: number, field: 'bonus' | 'eps', currentValue: number) => {
    // 가상 직급(VIRTUAL 상태)은 편집 불가
    const item = hrCostData.find(data => data.id === itemId);
    if (item?.employee?.status === 'VIRTUAL') {
      return;
    }
    setEditingCell({ itemId, field });
    setEditValues({ ...editValues, [`${itemId}-${field}`]: currentValue });
  };

  // 편집 값 변경 핸들러
  const handleEditChange = (itemId: number, field: 'bonus' | 'eps', value: string) => {
    const numValue = parseInt(value.replace(/[^0-9]/g, '')) || 0;
    setEditValues({ ...editValues, [`${itemId}-${field}`]: numValue });
  };

  // 편집 완료 핸들러
  const handleEditComplete = async (itemId: number, field: 'bonus' | 'eps') => {
    const key = `${itemId}-${field}`;
    const newValue = editValues[key];
    
    try {
      // 해당 item 찾기 (employeeId 필요)
      const item = hrCostData.find(item => item.id === itemId);
      if (!item || !item.employeeId) {
        return;
      }

      // API 호출용 데이터 준비
      const updateDto: { 
        bonusAmount?: number; 
        eps?: number;
      } = {};
      
      if (field === 'bonus') {
        updateDto.bonusAmount = newValue;
      } else if (field === 'eps') {
        updateDto.eps = newValue;
      }
      
      // Employee HR Cost API 호출 (employeeId 사용)
      const updatedItem = await employeeApi.updateHRCost(item.employeeId, selectedYear, updateDto);
      
      // 성공 시 로컬 데이터 업데이트 - 백엔드에서 재계산된 모든 값들 반영
      const updatedData = hrCostData.map(entry => {
        if (entry.id === itemId) {
          return {
            ...entry,
            // 직접 편집 가능한 값들
            bonusAmount: updatedItem.bonusAmount,
            eps: updatedItem.eps,
            // 계산된 기본 값들
            insuranceRetirement: updatedItem.insuranceRetirement,
            companyBurden: updatedItem.companyBurden,
            monthlyBurden: updatedItem.monthlyBurden,
            // 연관 계산된 값들 모두 업데이트
            monthlyEps: updatedItem.monthlyEps,
            fixedLaborCost: updatedItem.fixedLaborCost,
            monthlyLaborCost: updatedItem.monthlyLaborCost,
            adjustedMonthlyLaborCost: updatedItem.adjustedMonthlyLaborCost,
            ecm: updatedItem.ecm,
            finalLaborCost: updatedItem.finalLaborCost,
            adjustedFinalLaborCost: updatedItem.adjustedFinalLaborCost,
            // 상여금/성과급 관련 계산값들
            bonusBaseDays: updatedItem.bonusBaseDays,
            adjustedBonusRate: updatedItem.adjustedBonusRate,
            performanceBaseDays: updatedItem.performanceBaseDays,
            adjustedPerformanceRate: updatedItem.adjustedPerformanceRate,
            calculationDate: updatedItem.calculationDate,
          };
        }
        return entry;
      });

      setHRCostData(updatedData);
      setEditingCell(null);

      // 계산된 요약 데이터도 즉시 다시 가져오기 (백그라운드)
      calculatedSummaryApi.getHRCostYearlySummary(selectedYear)
        .then(summaryData => {
          if (summaryData) {
            setCalculatedTotals(summaryData.calculatedData as HRCostSummary);
          }
        })
        .catch(() => {
          // 에러 시 Frontend 계산 사용하도록 null 설정
          setCalculatedTotals(null);
        });
      
    } catch (error) {
      // 에러 발생 시 원래대로 복구
      setEditingCell(null);
      showToast('저장에 실패했습니다. 다시 시도해주세요.', 'error');
    }
  };

  // 편집 취소 핸들러
  const handleEditCancel = () => {
    setEditingCell(null);
    setEditValues({});
  };

  // EPS/ECM 비율 임시 값 변경 핸들러
  const handleTempRateChange = (type: 'eps' | 'ecm', value: number) => {
    if (type === 'eps') {
      setTempEpsRate(value);
    } else {
      setTempEcmRate(value);
    }
  };

  // EPS/ECM 비율 확정 핸들러 (포커스 잃거나 엔터 시 실행)
  const handleRateCommit = async (type: 'eps' | 'ecm') => {
    const previousEpsRate = epsRate;
    const previousEcmRate = ecmRate;
    const newValue = type === 'eps' ? tempEpsRate : tempEcmRate;
    const currentValue = type === 'eps' ? epsRate : ecmRate;
    
    // 값이 변경되지 않았으면 실행하지 않음
    if (newValue === currentValue) {
      return;
    }
    
    try {
      if (type === 'eps') {
        setEpsRate(newValue);
      } else {
        setEcmRate(newValue);
      }
      
      // API 호출로 비율 저장 및 HR Cost 데이터 재계산
      await employeeApi.updateHRCostSettings(selectedYear, {
        epsRate: (type === 'eps' ? newValue : epsRate) / 100, // 백분율을 소수로 변환 (30% -> 0.30)
        ecmRate: (type === 'ecm' ? newValue : ecmRate) / 100, // 백분율을 소수로 변환 (50% -> 0.50)
      });
      
      // 트리거 실행 완료를 위한 짧은 지연
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // 데이터 새로고침
      await fetchHRCostData();
    } catch (error) {
      // 에러 시 이전 값으로 되돌리기
      setEpsRate(previousEpsRate);
      setEcmRate(previousEcmRate);
      setTempEpsRate(previousEpsRate);
      setTempEcmRate(previousEcmRate);
    }
  };

  // 엔터키 핸들러
  const handleRateKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, type: 'eps' | 'ecm') => {
    if (e.key === 'Enter') {
      e.currentTarget.blur(); // 포커스 제거하여 onBlur 이벤트 트리거
    }
  };

  // 정렬된 데이터 - sortField가 null이면 백엔드 정렬 유지
  const sortedHRCostData = useMemo(() => {
    if (sortField === null) {
      // 백엔드 정렬을 그대로 유지
      return hrCostData;
    }
    return sortData(sortField, sortOrder);
  }, [hrCostData, sortField, sortOrder, sortData]);

  // DB에서 가져온 계산된 요약 데이터를 우선 사용
  const totals = useMemo(() => {
    // DB 계산 데이터가 있으면 항상 사용 (상세보기 여부 관계없이)
    if (calculatedTotals && !summaryLoading) {
      return {
        baseSalary: calculatedTotals.totalBaseSalary || 0,
        fourMajorInsAndSeverance: calculatedTotals.totalInsuranceRetirement || 0,
        companyContribution: calculatedTotals.totalCompanyBurden || 0,
        monthlyContribution: calculatedTotals.totalMonthlyBurden || 0,
        bonus: calculatedTotals.totalBonusAmount || 0,
        ps: 0, // PS는 계산된 요약에 없으므로 0으로 설정
        calculatedWelfareCost: calculatedTotals.totalWelfareCost || 0,
        fixedLaborCost: calculatedTotals.totalFixedLaborCost || 0,
        monthlyLaborCost: calculatedTotals.totalMonthlyLaborCost || 0,
        opexShare: calculatedTotals.totalOpexAllocation || 0,
        eps: calculatedTotals.totalEps || 0,
        monthlyEps: calculatedTotals.totalMonthlyEps || 0,
        ecm: calculatedTotals.totalEcm || 0,
        totalCost: calculatedTotals.totalFinalLaborCost || 0
      };
    }
    
    // DB에 계산된 요약 데이터가 없는 경우 빈 값 반환 (트리거가 계산 중)
    return { 
      baseSalary: 0, 
      fourMajorInsAndSeverance: 0,
      companyContribution: 0, 
      monthlyContribution: 0, 
      bonus: 0,
      ps: 0, 
      calculatedWelfareCost: 0, 
      fixedLaborCost: 0, 
      monthlyLaborCost: 0, 
      opexShare: 0, 
      eps: 0, 
      monthlyEps: 0,
      ecm: 0, 
      totalCost: 0 
    };
  }, [hrCostData, calculatedTotals, summaryLoading]);

  // 로딩 및 에러 처리
  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-full mx-auto">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">인력원가</h1>
            <p className="text-gray-600 mt-2">전체 직원의 인력원가 및 상세 비용 현황</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">직원 데이터를 불러오는 중...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-full mx-auto">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">인력원가</h1>
            <p className="text-gray-600 mt-2">전체 직원의 인력원가 및 상세 비용 현황</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-red-200 p-8">
            <div className="text-center">
              <div className="text-red-600 mb-4">
                <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <p className="text-red-600 font-medium">{error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                다시 시도
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-full mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">인력원가</h1>
            <p className="text-gray-600 mt-2">전체 직원의 인력원가 및 상세 비용 현황</p>
          </div>
        </div>

        {/* Fixed 버튼들 - 우상단 고정 */}
        <div className="fixed top-6 right-6 z-50 flex items-center gap-3">
          <button
            onClick={() => setShowDetailView(!showDetailView)}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2 min-w-[120px] justify-center shadow-lg"
          >
            {showDetailView ? '요약 보기' : '상세 보기'}
          </button>
          <button
            onClick={fetchHRCostData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 min-w-[120px] justify-center shadow-lg"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            새로고침
          </button>
        </div>

        {/* Reference Date Cards and Date Selector */}
        <div className="flex gap-4 mb-6 items-center">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-600 mb-1">상여금 기준일</h3>
            <p className="text-lg font-bold text-gray-900">{bonusRefDate}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-600 mb-1">성과급 기준일</h3>
            <p className="text-lg font-bold text-gray-900">{psRefDate}</p>
          </div>
          
          {/* EPS/ECM 비율 설정 */}
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-600 mb-2">EPS 비율</h3>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={tempEpsRate}
                onChange={(e) => handleTempRateChange('eps', Number(e.target.value))}
                onBlur={() => handleRateCommit('eps')}
                onKeyDown={(e) => handleRateKeyPress(e, 'eps')}
                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                min="0"
                max="100"
              />
              <span className="text-sm text-gray-600">%</span>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-600 mb-2">ECM 비율</h3>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={tempEcmRate}
                onChange={(e) => handleTempRateChange('ecm', Number(e.target.value))}
                onBlur={() => handleRateCommit('ecm')}
                onKeyDown={(e) => handleRateKeyPress(e, 'ecm')}
                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                min="0"
                max="100"
              />
              <span className="text-sm text-gray-600">%</span>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-600 mb-1">활성 직원 수</h3>
            <p className="text-lg font-bold text-blue-600">
              {hrCostData.filter(item => item.employee?.status === 'ACTIVE').length}명
            </p>
          </div>
          
          {/* 연도 선택기 */}
          <div className="flex items-center gap-2 bg-white p-4 rounded-lg shadow-sm border border-gray-200 ml-auto">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            >
              {availableYears.length > 0 ? (
                availableYears.filter(year => year >= 2025).map(year => (
                  <option key={year} value={year}>{year}년</option>
                ))
              ) : (
                <option value={currentYear}>{currentYear}년</option>
              )}
            </select>
          </div>
        </div>

        {/* 직원이 없을 때 메시지 */}
        {hrCostData.length === 0 && !loading && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="text-center">
              <div className="text-gray-400 mb-4">
                <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">활성 직원이 없습니다</h3>
              <p className="text-gray-500">인력원가를 계산할 활성 상태의 직원이 없습니다.</p>
            </div>
          </div>
        )}
        
        {/* Table */}
        {hrCostData.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table 
              ref={tableRef}
              className="w-full text-sm outline-none" 
              tabIndex={0}
              onKeyDown={handleKeyDown}
            >
              <thead className="bg-gray-50 text-gray-700 uppercase text-xs">
                <tr>
                  {(showDetailView ? detailHeaders : summaryHeaders).map((header, index) => {
                    // 정렬 가능한 컬럼인지 확인
                    let sortable = false;
                    let field: SortType | null = null;
                    
                    if (!showDetailView) {
                      // 요약 뷰의 정렬 가능한 컬럼들 (EPS 컬럼 추가로 인덱스 변경)
                      switch (index) {
                        case 2: field = 'name'; sortable = true; break;
                        case 3: field = 'position'; sortable = true; break;
                        case 4: field = 'finalCost'; sortable = true; break;
                        case 5: field = 'joinDate'; sortable = true; break;
                        case 6: field = 'salary'; sortable = true; break;
                        case 7: field = 'monthlyBurden'; sortable = true; break;
                        case 8: field = 'bonus'; sortable = true; break;
                        case 10: field = 'monthlyLaborCost'; sortable = true; break;
                        case 11: field = 'finalCost'; sortable = true; break;
                      }
                    } else {
                      // 상세 뷰의 정렬 가능한 컬럼들 (기본적으로 같은 필드들)
                      switch (index) {
                        case 1: field = 'name'; sortable = true; break;
                        case 2: field = 'position'; sortable = true; break;
                        case 3: field = 'finalCost'; sortable = true; break;
                        case 4: field = 'joinDate'; sortable = true; break;
                        case 5: field = 'salary'; sortable = true; break;
                      }
                    }

                    return (
                      <th 
                        key={header} 
                        className={`px-3 py-3 font-semibold text-center whitespace-nowrap sticky top-0 bg-gray-50 z-10 ${
                          sortable ? 'cursor-pointer hover:bg-gray-100 select-none' : ''
                        }`}
                        onClick={sortable && field ? () => handleSort(field!) : undefined}
                      >
                        <div className="flex items-center justify-center gap-1">
                          {header}
                          {sortable && field && sortField === field && (
                            <span className="text-blue-600">
                              {sortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {sortedHRCostData.map((item, rowIndex) => {
                  // 요약 뷰 데이터
                  const isVirtualPosition = item.employee?.status === 'VIRTUAL'; // VIRTUAL 상태는 가상 직급

                  const summaryData = [
                    { value: rowIndex + 1, align: 'text-center' },
                    { value: `${selectedYear}`, align: 'text-center', color: 'text-gray-600' },
                    { value: item.employee?.name || item.memo || 'Manager', align: 'text-center', fontWeight: 'font-medium' },
                    { value: item.employee?.position || item.memo || 'Manager', align: 'text-center' },
                    { value: formatRoundUpToMillions(Number(item.adjustedFinalLaborCost) || Number(item.finalLaborCost)), align: 'text-right', fontWeight: 'font-semibold', color: 'text-green-600' },
                    { value: isVirtualPosition ? '' : (item.joinDate ? new Date(item.joinDate).toISOString().split('T')[0] : '표준직급'), align: 'text-center' },
                    { value: formatCurrency(Number(item.annualSalary) || Number(item.employee?.annualSalary)), align: 'text-right' },
                    { value: formatCurrency(Number(item.monthlyBurden)), align: 'text-right' },
                    { value: formatCurrency(Number(item.bonusAmount)), align: 'text-right' },
                    { value: formatCurrency(Number(item.eps)), align: 'text-right' },
                    { value: formatCurrency(Number(item.monthlyLaborCost)), align: 'text-right', fontWeight: 'font-bold', color: 'text-indigo-600' },
                    { value: formatCurrency(Number(item.adjustedFinalLaborCost) || Number(item.finalLaborCost)), align: 'text-right', fontWeight: 'font-bold', color: 'text-blue-600' }
                  ];

                  // 상세 뷰 데이터
                  const detailData = [
                    { value: rowIndex + 1, align: 'text-center' },
                    { value: item.employee?.name || item.memo || 'Manager', align: 'text-center', fontWeight: 'font-medium' },
                    { value: item.employee?.position || item.memo || 'Manager', align: 'text-center' },
                    { value: formatRoundUpToMillions(Number(item.adjustedFinalLaborCost) || Number(item.finalLaborCost)), align: 'text-right', fontWeight: 'font-semibold', color: 'text-green-600' },
                    { value: isVirtualPosition ? '' : (item.joinDate ? new Date(item.joinDate).toISOString().split('T')[0] : '표준직급'), align: 'text-center' },
                    { value: formatCurrency(Number(item.annualSalary) || Number(item.employee?.annualSalary)), align: 'text-right' },
                    { value: formatCurrency(Number(item.insuranceRetirement)), align: 'text-right' },
                    { value: formatCurrency(Number(item.companyBurden)), align: 'text-right' },
                    { value: formatCurrency(Number(item.monthlyBurden)), align: 'text-right' },
                    { value: `${item.bonusBaseDays}일`, align: 'text-center' },
                    { value: `${(Number(item.adjustedBonusRate || item.bonusRate) * 100).toFixed(2)}%`, align: 'text-center' },
                    { value: `${item.performanceBaseDays}일`, align: 'text-center' },
                    { value: `${(Number(item.adjustedPerformanceRate || item.performanceRate) * 100).toFixed(2)}%`, align: 'text-center' },
                    { value: formatCurrency(Number(item.bonusAmount)), align: 'text-right' },
                    { value: formatCurrency(Number(item.welfareCost)), align: 'text-right' },
                    { value: formatCurrency(Number(item.fixedLaborCost)), align: 'text-right', fontWeight: 'font-semibold' },
                    { value: formatCurrency(Number(item.monthlyLaborCost)), align: 'text-right', fontWeight: 'font-bold', color: 'text-indigo-600' },
                    { value: formatCurrency(Number(item.opexAllocation)), align: 'text-right' },
                    { value: formatCurrency(Number(item.eps)), align: 'text-right' },
                    { value: formatCurrency(Number(item.monthlyEps)), align: 'text-right' },
                    { value: formatCurrency(Number(item.ecm)), align: 'text-right' },
                    { value: formatCurrency(Number(item.adjustedFinalLaborCost) || Number(item.finalLaborCost)), align: 'text-right', fontWeight: 'font-bold', color: 'text-blue-600' }
                  ];

                  const cellData = showDetailView ? detailData : summaryData;

                  return (
                    <tr key={item.employee?.id || item.id} className={isVirtualPosition ? 'bg-slate-100' : ''}>
                      {cellData.map((cell, colIndex) => {
                        // 편집 가능한 칼럼 확인 (가상 직급은 편집 불가)
                        let isEditable = false;
                        let editableField: 'bonus' | 'eps' | null = null;
                        
                        if (!showDetailView) {
                          // 요약 뷰에서만 편집 가능
                          if (colIndex === 8) { editableField = 'bonus'; isEditable = true; }
                          else if (colIndex === 9) { editableField = 'eps'; isEditable = true; }
                        }
                        // 상세 뷰에서는 편집 불가능
                        
                        isEditable = isEditable && !isVirtualPosition;
                        const isEditing = editingCell && editingCell.itemId === item.id && editingCell.field === editableField;
                        
                        if (isEditing) {
                          // 편집 모드
                          const field = editingCell!.field;
                          const key = `${item.id}-${field}`;
                          const value = editValues[key] || 0;
                          
                          return (
                            <td 
                              key={colIndex}
                              className={`${getCellStyle(rowIndex, colIndex)} ${cell.align} px-1 py-1`}
                              data-row={rowIndex}
                              data-col={colIndex}
                            >
                              <input
                                type="text"
                                value={formatCurrency(value)}
                                onChange={(e) => handleEditChange(item.id, field, e.target.value)}
                                onBlur={() => handleEditComplete(item.id, field)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleEditComplete(item.id, field);
                                  } else if (e.key === 'Escape') {
                                    handleEditCancel();
                                  }
                                }}
                                className="w-full px-1 py-0 text-right text-sm border border-blue-500 rounded focus:outline-none focus:border-blue-600"
                                autoFocus
                              />
                            </td>
                          );
                        }
                        
                        return (
                          <td 
                            key={colIndex}
                            className={`${getCellStyle(rowIndex, colIndex)} ${cell.align} ${cell.fontWeight || ''} ${cell.color || ''} cursor-pointer select-none ${isEditable ? 'hover:bg-blue-50' : ''}`}
                            onClick={() => {
                              if (isEditable && editableField) {
                                const currentValue = editableField === 'bonus'
                                  ? Number(item.bonusAmount)
                                  : Number(item.eps);
                                handleEditStart(item.id, editableField, currentValue);
                              } else {
                                handleCellClick(rowIndex, colIndex);
                              }
                            }}
                            onDoubleClick={() => {
                              if (isEditable && editableField) {
                                const currentValue = editableField === 'bonus'
                                  ? Number(item.bonusAmount)
                                  : Number(item.eps);
                                handleEditStart(item.id, editableField, currentValue);
                              }
                            }}
                            data-row={rowIndex}
                            data-col={colIndex}
                            title={isEditable ? '클릭하여 편집' : ''}
                          >
                            {cell.value}
                            {isEditable && <span className="ml-1 text-xs text-gray-400">✏️</span>}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                
                {/* Total Row */}
                <tr>
                  {showDetailView ? (
                    // 상세 뷰 합계 행
                    <>
                      <td 
                        colSpan={3} 
                        className={`${getCellStyle(hrCostData.length, 0, true)} text-center cursor-pointer select-none`}
                        onClick={() => handleCellClick(hrCostData.length, 0)}
                        data-row={hrCostData.length}
                        data-col={0}
                      >
                        합계
                      </td>
                      <td className={`${getCellStyle(hrCostData.length, 3, true)} text-right text-green-700 cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 3)} data-row={hrCostData.length} data-col={3}>{formatCurrency(totals.totalCost)}</td>
                      <td className={`${getCellStyle(hrCostData.length, 4, true)} cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 4)} data-row={hrCostData.length} data-col={4}></td>
                      <td className={`${getCellStyle(hrCostData.length, 5, true)} text-right cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 5)} data-row={hrCostData.length} data-col={5}>{formatCurrency(totals.baseSalary)}</td>
                      <td className={`${getCellStyle(hrCostData.length, 6, true)} text-right cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 6)} data-row={hrCostData.length} data-col={6}>{formatCurrency(totals.fourMajorInsAndSeverance)}</td>
                      <td className={`${getCellStyle(hrCostData.length, 7, true)} text-right cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 7)} data-row={hrCostData.length} data-col={7}>{formatCurrency(totals.companyContribution)}</td>
                      <td className={`${getCellStyle(hrCostData.length, 8, true)} text-right cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 8)} data-row={hrCostData.length} data-col={8}>{formatCurrency(totals.monthlyContribution)}</td>
                      <td className={`${getCellStyle(hrCostData.length, 9, true)} cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 9)} data-row={hrCostData.length} data-col={9}></td>
                      <td className={`${getCellStyle(hrCostData.length, 10, true)} cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 10)} data-row={hrCostData.length} data-col={10}></td>
                      <td className={`${getCellStyle(hrCostData.length, 11, true)} cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 11)} data-row={hrCostData.length} data-col={11}></td>
                      <td className={`${getCellStyle(hrCostData.length, 12, true)} cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 12)} data-row={hrCostData.length} data-col={12}></td>
                      <td className={`${getCellStyle(hrCostData.length, 13, true)} text-right cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 13)} data-row={hrCostData.length} data-col={13}>{formatCurrency(totals.bonus)}</td>
                      <td className={`${getCellStyle(hrCostData.length, 14, true)} text-right cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 14)} data-row={hrCostData.length} data-col={14}>{formatCurrency(totals.calculatedWelfareCost)}</td>
                      <td className={`${getCellStyle(hrCostData.length, 15, true)} text-right cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 15)} data-row={hrCostData.length} data-col={15}>{formatCurrency(totals.fixedLaborCost)}</td>
                      <td className={`${getCellStyle(hrCostData.length, 16, true)} text-right text-indigo-700 cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 16)} data-row={hrCostData.length} data-col={16}>{formatCurrency(totals.monthlyLaborCost)}</td>
                      <td className={`${getCellStyle(hrCostData.length, 17, true)} text-right cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 17)} data-row={hrCostData.length} data-col={17}>{formatCurrency(totals.opexShare)}</td>
                      <td className={`${getCellStyle(hrCostData.length, 18, true)} text-right cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 18)} data-row={hrCostData.length} data-col={18}>{formatCurrency(totals.eps)}</td>
                      <td className={`${getCellStyle(hrCostData.length, 19, true)} text-right cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 19)} data-row={hrCostData.length} data-col={19}>{formatCurrency(totals.monthlyEps)}</td>
                      <td className={`${getCellStyle(hrCostData.length, 20, true)} text-right cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 20)} data-row={hrCostData.length} data-col={20}>{formatCurrency(totals.ecm)}</td>
                      <td className={`${getCellStyle(hrCostData.length, 21, true)} text-right text-blue-700 cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 21)} data-row={hrCostData.length} data-col={21}>{formatCurrency(totals.totalCost)}</td>
                    </>
                  ) : (
                    // 요약 뷰 합계 행
                    <>
                      <td className={`${getCellStyle(hrCostData.length, 0, true)} text-center cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 0)} data-row={hrCostData.length} data-col={0}>합계</td>
                      <td 
                        colSpan={2} 
                        className={`${getCellStyle(hrCostData.length, 1, true)} text-center cursor-pointer select-none`}
                        onClick={() => handleCellClick(hrCostData.length, 1)}
                        data-row={hrCostData.length}
                        data-col={1}
                      >
                        {`${selectedYear}`}
                      </td>
                      <td className={`${getCellStyle(hrCostData.length, 3, true)} text-center cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 3)} data-row={hrCostData.length} data-col={3}></td>
                      <td className={`${getCellStyle(hrCostData.length, 4, true)} text-right text-green-700 cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 4)} data-row={hrCostData.length} data-col={4}>{formatCurrency(totals.totalCost)}</td>
                      <td className={`${getCellStyle(hrCostData.length, 5, true)} text-right cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 5)} data-row={hrCostData.length} data-col={5}></td>
                      <td className={`${getCellStyle(hrCostData.length, 6, true)} text-right cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 6)} data-row={hrCostData.length} data-col={6}>{formatCurrency(totals.baseSalary)}</td>
                      <td className={`${getCellStyle(hrCostData.length, 7, true)} text-right cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 7)} data-row={hrCostData.length} data-col={7}>{formatCurrency(totals.monthlyContribution)}</td>
                      <td className={`${getCellStyle(hrCostData.length, 8, true)} text-right cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 8)} data-row={hrCostData.length} data-col={8}>{formatCurrency(totals.bonus)}</td>
                      <td className={`${getCellStyle(hrCostData.length, 9, true)} text-right cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 9)} data-row={hrCostData.length} data-col={9}>{formatCurrency(totals.eps)}</td>
                      <td className={`${getCellStyle(hrCostData.length, 10, true)} text-right text-indigo-700 cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 10)} data-row={hrCostData.length} data-col={10}>{formatCurrency(totals.monthlyLaborCost)}</td>
                      <td className={`${getCellStyle(hrCostData.length, 11, true)} text-right text-blue-700 cursor-pointer select-none`} onClick={() => handleCellClick(hrCostData.length, 11)} data-row={hrCostData.length} data-col={11}>{formatCurrency(totals.totalCost)}</td>
                    </>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        )}
        
      </div>
    </div>
  );
};

export default HRCostManagement;